"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn, formatDate } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { locales } from "@/lib/constants";
import { useTranslation } from "react-i18next";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  hideCaption?: boolean;
  dayFooter?: (date: Date) => React.ReactNode;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  hideCaption = false,
  dayFooter = () => null,
  month,
  ...props
}: CalendarProps) {
  const { i18n } = useTranslation();
  const locale = locales[i18n.language as keyof typeof locales];
  return (
    <DayPicker
      locale={locale}
      month={month}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: `flex flex-col sm:flex-row ${
          !hideCaption ? "space-y-4 sm:space-x-4 sm:space-y-0" : ""
        }`,
        month: !hideCaption ? "space-y-4" : "",
        caption: `flex justify-center pt-1 relative items-center ${
          hideCaption ? "hidden" : ""
        }`,
        caption_start: "w-full",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse",
        head: "flex items-center h-[30px] w-full",
        head_row: "flex gap-1 w-full",
        head_cell:
          "text-white/40 flex-1 uppercase rounded-md font-medium leading-5 text-[13px]",
        row: "flex w-full mt-[6px] gap-1",
        cell: cn(
          "relative p-0 w-full text-center text-[15px] font-medium focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: "h-11 w-full p-0 font-medium aria-selected:opacity-100 text-white rounded-lg",
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "!bg-white !text-[#041935] hover:bg-white/90 focus-visible:outline-none hover:text-[#041935] focus:bg-white focus:text-[#041935]",
        day_today: "bg-[#041935]/30 text-[#041935]",
        day_outside:
          "day-outside text-white/30 text-[15px] font-medium leading-5 aria-selected:text-[#041935] aria-selected:bg-white/60",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
        DayContent: (props) => {
          return (
            <div
              data-testid={`calendar-day-${formatDate(props.date, "yyyy-MM-dd")}`}
            >
              {props.date.getDate()}
              {dayFooter && dayFooter(props.date)}
            </div>
          );
        },
        ...props.components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
